import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  InfoAlert,
  SuccessAlert,
  Table,
  WaitingAlert,
} from '@tmapy/style-guide';

import {
  changeDeleteSuccessAlertVisibility,
  changeSubmitSuccessAlertVisibility,
} from '../../app/actions';
import { FilterBtn } from '../../app/components/FilterBtn';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { QUERY_STATE } from '../../constants';
import { useHistoryAction, useSelector } from '../../hooks';
import { usePriceListItemsListResource } from '../../openapi/operations/priceListItemsList';
import { stripEmptyFilters } from '../../utils/queryArgs';

import { PriceListItemFilter } from './PriceListItemFilter';
import { PriceListItemListItem } from './PriceListItemListItem';
import { priceListMessages as msg, priceListBtnMessages } from './priceListMessages';

export const PriceListItemList: React.FC = () => {
  const dispatch = useDispatch();
  const navigationType = useNavigationType();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const filters = useSelector((state) => state.filter.priceListItemList);
  const showItemCreatedAlert = useSelector((state) => state.app.submitSuccessAlertVisible);
  const showItemDeletedAlert = useSelector((state) => state.app.deleteSuccessAlertVisible);

  const query = stripEmptyFilters(filters);

  const { queryState, data, fetchMore } = usePriceListItemsListResource(
    query,
    navigationType !== 'PUSH',
  );

  const priceListItems = data?.results ?? [];
  const priceListItemCount = data?.count;

  const handleNewPriceListItemBtnClick = useHistoryAction('vydejNewPriceListItem');

  const handleClearStates = useCallback(() => {
    dispatch(changeSubmitSuccessAlertVisibility(false));
    dispatch(changeDeleteSuccessAlertVisibility(false));
  }, [dispatch]);

  const handleNextPriceListItemsBtnClick = useCallback(() => {
    handleClearStates();
    if (data?.next) {
      fetchMore(data.next);
    }
  }, [handleClearStates, fetchMore, data]);

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  // component unmount
  useEffect(
    () => () => {
      handleClearStates();
    },
    [handleClearStates],
  );

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(priceListBtnMessages.filter)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='pricelist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...msg.priceListPageTitle} />
            </PageTitle>
            <span className='sg-a-f-a' />
            <BtnBar>
              <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />
              <CtaBtn onClick={handleNewPriceListItemBtnClick} size='sml'>
                <FormattedMessage {...msg.priceListBtnNew} />
              </CtaBtn>
            </BtnBar>
          </header>
          {showItemCreatedAlert && (
            <SuccessAlert>
              <FormattedMessage {...msg.priceListItemCreated} />
            </SuccessAlert>
          )}
          {showItemDeletedAlert && (
            <SuccessAlert>
              <FormattedMessage {...msg.priceListItemDeleted} />
            </SuccessAlert>
          )}
          {queryState === QUERY_STATE.WAITING && priceListItems.length < 1 && (
            <WaitingAlert>
              <FormattedMessage {...msg.priceListStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && priceListItems.length < 1 && (
            <DangerAlert>
              <FormattedMessage {...msg.priceListStateFail} />
            </DangerAlert>
          )}
          {priceListItems.length > 0 ? (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th>
                        <FormattedMessage {...msg.name} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.type} />
                      </th>
                      <th>
                        <FormattedMessage {...msg.unit} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.unitPrice} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.vatRate} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.unitPriceWithVat} />
                      </th>
                      <th className='sg-a-ta-r'>
                        <FormattedMessage {...msg.created} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceListItems.map((priceListItem) => (
                      <PriceListItemListItem key={priceListItem.id} {...priceListItem} />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={priceListItems.length}
                total={priceListItemCount!}
                queryState={queryState}
                onNextBtnClick={handleNextPriceListItemsBtnClick}
              />
            </div>
          ) : (
            queryState === QUERY_STATE.SUCCESS && (
              <InfoAlert>
                <FormattedMessage
                  {...(isFiltered ? msg.priceListStateFilteredEmpty : msg.priceListStateEmpty)}
                />
              </InfoAlert>
            )
          )}
        </article>
      </MainContent>
      <PriceListItemFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
